import { useState } from 'react'
import axios from 'axios'
import { useQuery } from '@tanstack/react-query'
import useRouter from './useRouter'
import { API_BASE_URL } from '../constants/system'
import { canEditStoryBoard, pickDashboardStoryArtifact, type ApiArtifactRow } from '../utils/storyBoardEditing'
import type { WebappDashboardConfig } from '../types/gotypes'

export const NOT_YOURS_TO_EDIT = 'Only the person who made this story board, or a Manager, can edit it.'

interface Viewer {
  isManager: boolean
  userEmail: string
  /** Worth asking at all: a Manager, or the person who published the dashboard. */
  mayTry: boolean
}

// "Edit story board" on a published web app dashboard (#1416): find the
// session's web app artifact this dashboard shows, then open the session page
// with the editor on it (?editStory=<artifactId>). Publishing from there
// updates this dashboard.
//
// Who may edit is the SESSION owner or a Manager, and publishing a colleague's
// session makes you the dashboard's owner, not the session's. The artifact
// list (one small request, only for a Manager or the publisher) carries the
// session owner as each artifact's user_email, so the button is shown only
// to someone the editor will open for. openEditor still asks the story route
// itself before navigating: the server decides.
export default function useStoryBoardEditLink(config: WebappDashboardConfig | null | undefined, viewer: Viewer) {
  const router = useRouter()
  const [opening, setOpening] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const sessionId = config?.session_id || ''

  const artifactsQuery = useQuery({
    queryKey: ['story-board-session-artifacts', sessionId],
    enabled: !!sessionId && viewer.mayTry,
    retry: false,
    staleTime: 60_000,
    queryFn: async (): Promise<ApiArtifactRow[]> => {
      const res = await axios.get<{ artifacts: ApiArtifactRow[] }>(
        `${API_BASE_URL}/agent/session/${encodeURIComponent(sessionId)}/artifacts`,
      )
      return res.data.artifacts || []
    },
  })
  const row = artifactsQuery.data ? pickDashboardStoryArtifact(artifactsQuery.data, config?.dist_path) : null
  const canEdit = !!sessionId && viewer.mayTry && (
    viewer.isManager || (!!row && canEditStoryBoard(false, row.user_email, viewer.userEmail))
  )

  const openEditor = async (customer: string) => {
    if (!sessionId) return
    setError(null)
    setOpening(true)
    try {
      const rows = artifactsQuery.data ?? (await artifactsQuery.refetch()).data ?? []
      const target = pickDashboardStoryArtifact(rows, config?.dist_path)
      if (!target) {
        setError('The story board this dashboard shows could not be found in its session.')
        return
      }
      // The story route answers 404 to anyone who may not edit.
      await axios.get(`${API_BASE_URL}/agent/artifacts/${encodeURIComponent(target.id)}/story`)
      router.navigate('agent-session', { customer, sessionId, editStory: target.id })
    } catch (err: any) {
      const status = err?.response?.status
      setError(status === 404 || status === 403
        ? NOT_YOURS_TO_EDIT
        : (err?.response?.data?.error || err?.message || 'The story board could not be opened.'))
    } finally {
      setOpening(false)
    }
  }

  return { canEdit, openEditor, opening, error, clearError: () => setError(null) }
}
